import React, { useState, useEffect, useCallback } from "react";
import {
  Copy,
  Rocket,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  AlertTriangle,
  X,
  Play,
  FileText,
  Folder,
  Plus,
  Image,
  Globe,
  Calendar,
  Clock,
  Video,
  Activity,
  TrendingUp,
  ArrowRight,
  PlayCircle,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import dashboardService from "../../services/dashboardServices";
import { toast } from "react-toastify";
import folderService from "../../services/folderServices";
import {
  createScheduledMeeting,
  getScheduleMeeting,
  deleteScheduledMeeting,
  checkCreateRoomAailability,
} from "../../services/roomServices";
import { deleteMeetingRoom } from "../../services/roomServices";
import ScheduleMeetingForm from "./forms/ScheduleMeetingForm";
import useIsMobile from "../../hooks/useIsMobile";
import WebinarScheduleForm from "./forms/WebinarScheduleForm";
import { getTimezoneZone, TIMEZONES } from "../../utils/timezones";
import { useSiteContext } from "../../context/SiteContext";

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, bgColor }) => {
  return (
    <div className="bg-white/5 rounded-xl border border-white/10 p-4">
      <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-white mb-1 font-sans">{value}</p>
      <p className="text-sm text-gray-400 font-sans">{label}</p>
    </div>
  );
};

const DashboardHome = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user } = useSiteContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rooms, setRooms] = useState([]);
  const [totalRooms, setTotalRooms] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [meetingName, setMeetingName] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [meetingEndTime, setMeetingEndTime] = useState("");

  // Stats state
  const [stats, setStats] = useState({
    todaySessions: 0,
    upcomingSessions: 0,
    activeRooms: 0,
    totalRecordings: 0,
    totalMinutes: 0,
    storageUsed: 0,
  });
  const [recordings, setRecordings] = useState([]);
  const [monthlyUsage, setMonthlyUsage] = useState({ recordingMinutes: 0, storageGB: 0, rooms: 0 });

  // Initialize timezone from system timezone
  const getInitialTimezone = () => {
    const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matchingTz = TIMEZONES.find(
      (tz) => tz.name === systemTz || tz.zone === systemTz
    );
    return matchingTz ? matchingTz.value : "America/New_York";
  };
  const [selectedTimezone, setSelectedTimezone] = useState(
    getInitialTimezone()
  );
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);
  const [speakersList, setSpeakersList] = useState([]);
  const [roomSettings, setRoomSettings] = useState({
    allowChat: true,
    allowScreenSharing: true,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    requirePassword: false,
    password: "",
    hostApprovalRequired: false,
    saveAsPreset: false,
    presetName: "",
  });
  const [customBackgroundEnabled, setCustomBackgroundEnabled] = useState(false);
  const [backgroundType, setBackgroundType] = useState("solid");
  const [backgroundColor, setBackgroundColor] = useState("#1a1a2e");
  const [gradientColor1, setGradientColor1] = useState("#1a1a2e");
  const [gradientColor2, setGradientColor2] = useState("#615fff");
  const [gradientDirection, setGradientDirection] = useState("to right");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);
  const [exitUrlEnabled, setExitUrlEnabled] = useState(false);
  const [exitUrl, setExitUrl] = useState("");
  const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
  const [customButtonUrl, setCustomButtonUrl] = useState("");
  const [customButtonText, setCustomButtonText] = useState("");
  const [waitingRoomTextEnabled, setWaitingRoomTextEnabled] = useState(false);
  const [waitingRoomTitle, setWaitingRoomTitle] = useState("");
  const [waitingRoomMessage, setWaitingRoomMessage] = useState("");
  const [waitingRoomSubText, setWaitingRoomSubText] = useState("");
  const [elementPresets, setElementPresets] = useState([]);
  const [showItem, setShowItem] = useState(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showEditPresetModal, setShowEditPresetModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editElementPresets, setEditElementPresets] = useState([]);
  const [editDraggedItem, setEditDraggedItem] = useState(null);
  const [editDragOverIndex, setEditDragOverIndex] = useState(null);
  const [roomCreationAvailable, setRoomCreationAvailable] = useState(false);

  const navigate = useNavigate();

  // Reset form when navigating to dashboard
  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setShowItem(null);
    }
  }, [location.pathname]);

  // Listen for dashboard click event
  useEffect(() => {
    const handleDashboardClick = () => {
      setShowItem(null);
    };

    window.addEventListener("dashboard-clicked", handleDashboardClick);

    return () => {
      window.removeEventListener("dashboard-clicked", handleDashboardClick);
    };
  }, []);

  // Helper function to validate URL
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  const dashboardData = useCallback(async () => {
    try {
      const response = await dashboardService.getDashboardData(
        currentPage,
        rowsPerPage
      );
      // Handle both response shapes: {success, data: {...}} or direct {...}
      const data = response.data?.data || response.data;
      if (data) {
        setStats((prev) => ({
          ...prev,
          activeRooms: data.rooms || 0,
          totalRecordings: data.recordings || 0,
          totalMinutes: data.monthlyUsage?.recordingMinutes || 0,
          storageUsed: data.monthlyUsage?.storageGB || 0,
        }));
        setMonthlyUsage({
          recordingMinutes: data.monthlyUsage?.recordingMinutes || 0,
          storageGB: data.monthlyUsage?.storageGB || 0,
          rooms: data.rooms || 0,
        });
      }
    } catch (err) {
      console.log('[DashboardHome] Error:', err);
    }
  }, [currentPage, rowsPerPage]);

  const getRooms = useCallback(async () => {
    try {
      const response = await getScheduleMeeting(currentPage, rowsPerPage);
      const roomsData = response.data || [];
      setRooms(roomsData);

      // Calculate stats from rooms
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      let todayCount = 0;
      let upcomingCount = 0;

      roomsData.forEach((room) => {
        if (room.scheduledStartTime) {
          const roomDate = new Date(room.scheduledStartTime);
          if (roomDate >= today && roomDate < tomorrow) {
            todayCount++;
          }
          if (roomDate > now) {
            upcomingCount++;
          }
        }
      });

      setStats((prev) => ({
        ...prev,
        todaySessions: todayCount,
        upcomingSessions: upcomingCount,
      }));
    } catch (err) {
      console.log(err);
      setRooms([]);
      setTotalPages(1);
    }
  }, [currentPage, rowsPerPage]);

  const getFolders = useCallback(async () => {
    try {
      const response = await dashboardService.getFolders();
      const foldersData = response.data || [];
      setFolders(foldersData);
      if (foldersData.length > 0) {
        setSelectedFolder(foldersData[0]._id || foldersData[0].id);
      }
    } catch (err) {
      console.log("Error fetching folders:", err);
      setFolders([]);
    }
  }, []);

  const checkRoomCreationAvailability = useCallback(async () => {
    try {
      const response = await checkCreateRoomAailability();
      setRoomCreationAvailable(response.data.available);
    } catch (err) {
      console.log(err);
      setRoomCreationAvailable(false);
    }
  }, []);

  useEffect(() => {
    dashboardData();
    getRooms();
    getFolders();
    checkRoomCreationAvailability();
  }, [
    currentPage,
    rowsPerPage,
    dashboardData,
    getRooms,
    getFolders,
    checkRoomCreationAvailability,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest("[data-dropdown]")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleLaunchRoom = (room) => {
    window.open(`${window.location.origin}/${room.roomId}`, "_blank");
  };

  const handleCopyRoom = (room) => {
    navigator.clipboard.writeText(`${window.location.origin}/${room.roomId}`);
    toast.success("Room link copied to clipboard");
  };

  const handleEditRoom = (room) => {
    setOpenDropdown(null);
    if (room.roomType === "Webinar") {
      navigate(`/dashboard/edit-webinar/${room.id}`);
    } else {
      navigate(`/dashboard/edit-schedule-meeting/${room.id}`);
    }
  };

  const handleDeleteRoom = (room) => {
    setRoomToDelete(room);
    setShowDeleteModal(true);
    setOpenDropdown(null);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      await deleteScheduledMeeting(roomToDelete.id);
      getRooms();
      dashboardData();
      setShowDeleteModal(false);
      setRoomToDelete(null);
      toast.success("Schedule meeting deleted successfully");
    } catch (err) {
      console.error("Error deleting room:", err);
      alert("Error deleting room. Please try again.");
    }
  };

  const cancelDeleteRoom = () => {
    setShowDeleteModal(false);
    setRoomToDelete(null);
  };

  const toggleDropdown = (roomName) => {
    setOpenDropdown(openDropdown === roomName ? null : roomName);
  };

  const getRoomType = (roomType) => {
    return String(roomType).toLowerCase() === "conferencing"
      ? "Meeting"
      : roomType;
  };

  const handleCreateMeeting = () => {
    if (showItem === "meeting") {
      setShowItem(null);
      return;
    } else {
      setShowItem("meeting");
    }
  };

  const handleLiveWebinar = () => {
    if (showItem === "webinar") {
      setShowItem(null);
      return;
    } else {
      setShowItem("webinar");
    }
  };

  const handleSettingChange = (setting, value) => {
    setRoomSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const removeElementPreset = (id) => {
    setElementPresets((prev) => prev.filter((preset) => preset.id !== id));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, dropIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(dropIndex);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    const newPresets = [...elementPresets];
    const draggedPreset = newPresets[draggedItem];
    newPresets.splice(draggedItem, 1);
    newPresets.splice(dropIndex, 0, draggedPreset);

    setElementPresets(newPresets);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleEditPreset = (room) => {
    setEditingRoom(room);
    setShowEditPresetModal(true);
    getPresetsByRoomId(room.room);
  };

  const handleAddPresetToRoom = (element) => {
    const isDuplicate = editElementPresets.some(
      (preset) =>
        preset.presetId === (element._id || element.id) ||
        preset.id === (element._id || element.id) ||
        preset._id === (element._id || element.id)
    );

    if (isDuplicate) {
      toast.warning("This preset is already added");
      return;
    }

    const newId = Math.max(...editElementPresets.map((p) => p.id), 0) + 1;
    setEditElementPresets((prev) => [
      ...prev,
      {
        id: newId,
        name: element.name,
        type: element.type || "square",
        presetId: element._id || element.id,
        folderId: element.folderId || "",
      },
    ]);
  };

  const handleRemovePresetFromRoom = (presetToRemove) => {
    setEditElementPresets((prev) => {
      const filtered = prev.filter((preset) => {
        return !(
          preset.id === presetToRemove.id &&
          preset.presetId === presetToRemove.presetId &&
          preset.name === presetToRemove.name
        );
      });
      return filtered;
    });
  };

  const handleSaveRoomPresets = async () => {
    if (!editingRoom) return;
    try {
      await dashboardService.updateRoomPresets(
        editingRoom.room,
        editingRoom.name,
        editElementPresets
      );

      toast.success("Room presets updated successfully!");
      getRooms();
      setShowEditPresetModal(false);
      setEditingRoom(null);
      setEditElementPresets([]);
    } catch (error) {
      console.error("Error updating room presets:", error);
      toast.error("Error updating room presets");
    }
  };

  // Drag and drop handlers for edit preset modal
  const handleEditDragStart = (e, index) => {
    setEditDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleEditDragOver = (e, dropIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setEditDragOverIndex(dropIndex);
  };

  const handleEditDragLeave = () => {
    setEditDragOverIndex(null);
  };

  const handleEditDrop = (e, dropIndex) => {
    e.preventDefault();

    if (editDraggedItem === null || editDraggedItem === dropIndex) {
      setEditDraggedItem(null);
      setEditDragOverIndex(null);
      return;
    }

    const newPresets = [...editElementPresets];
    const draggedPreset = newPresets[editDraggedItem];
    newPresets.splice(editDraggedItem, 1);
    newPresets.splice(dropIndex, 0, draggedPreset);

    setEditElementPresets(newPresets);
    setEditDraggedItem(null);
    setEditDragOverIndex(null);
  };

  const handleEditDragEnd = () => {
    setEditDraggedItem(null);
    setEditDragOverIndex(null);
  };

  const addElementPreset = () => {
    setShowPresetModal(true);
  };

  const selectPreset = (element) => {
    const isDuplicate = elementPresets.some(
      (preset) => preset.presetId === (element._id || element.id)
    );

    if (isDuplicate) {
      return;
    }

    const newId = Math.max(...elementPresets.map((p) => p.id), 0) + 1;
    setElementPresets((prev) => [
      ...prev,
      {
        id: newId,
        name: element.name,
        type: element.type || "square",
        presetId: element._id || element.id,
        folderId: element.folderId || "",
      },
    ]);
  };

  const getWidgetIcon = (type, colorcss = "text-black") => {
    switch (type) {
      case "document":
        return <FileText size={16} className={colorcss} />;
      case "video":
        return <Play size={16} className={colorcss} />;
      case "image":
        return <Image size={16} className={colorcss} />;
      case "cta":
        return <Rocket size={16} className={colorcss} />;
      case "website":
        return <Globe size={16} className={colorcss} />;
      default:
        return <FileText size={16} className={colorcss} />;
    }
  };

  const handleStartMeeting = async (isnow = false) => {
    const roomType = showItem === "webinar" ? "Webinar" : "Meeting";
    const roomTypeLower = roomType.toLowerCase();

    // Validation
    if (!meetingName.trim()) {
      toast.error(`Please enter a ${roomTypeLower} name`);
      return;
    }

    if (meetingName.trim().length < 3) {
      toast.error(`${roomType} name must be at least 3 characters long`);
      return;
    }

    if (roomSettings.requirePassword && !roomSettings.password.trim()) {
      toast.error(`Please enter a password for the ${roomTypeLower}`);
      return;
    }

    if (roomSettings.saveAsPreset && !roomSettings.presetName.trim()) {
      toast.error("Please enter a preset name");
      return;
    }

    if (exitUrlEnabled) {
      if (!exitUrl.trim()) {
        toast.error("Please enter an exit URL");
        return;
      }
      try {
        new URL(exitUrl.trim());
      } catch (e) {
        toast.error("Please enter a valid URL (e.g., https://example.com)");
        return;
      }
    }

    if (customButtonEnabled) {
      if (!customButtonText.trim()) {
        toast.error("Please enter a button name");
        return;
      }
      if (customButtonText.trim().length < 3) {
        toast.error("Button name must be at least 3 characters long");
        return;
      }
      if (!customButtonUrl.trim()) {
        toast.error("Please enter a button URL");
        return;
      }
      try {
        new URL(customButtonUrl.trim());
      } catch (e) {
        toast.error(
          "Please enter a valid button URL (e.g., https://example.com)"
        );
        return;
      }
    }

    if (waitingRoomTextEnabled) {
      if (!waitingRoomTitle.trim()) {
        toast.error("Please enter waiting room title");
        return;
      }
      if (waitingRoomTitle.trim().length < 3) {
        toast.error("Waiting room title must be at least 3 characters long");
        return;
      }
      if (!waitingRoomMessage.trim()) {
        toast.error("Please enter waiting room message");
        return;
      }
      if (waitingRoomMessage.trim().length < 3) {
        toast.error("Waiting room message must be at least 3 characters long");
        return;
      }
      if (!waitingRoomSubText.trim()) {
        toast.error("Please enter waiting room sub text");
        return;
      }
      if (waitingRoomSubText.trim().length < 3) {
        toast.error("Waiting room sub text must be at least 3 characters long");
        return;
      }
    }

    if (customBackgroundEnabled) {
      if (backgroundType === "image" && !backgroundImage) {
        toast.error("Please select a background image");
        return;
      }
      if (backgroundType === "image" && backgroundImage) {
        const maxSize = 5 * 1024 * 1024;
        if (backgroundImage.size > maxSize) {
          toast.error("Background image size should not exceed 5MB");
          return;
        }
      }
      if (backgroundType === "gradient") {
        if (!gradientColor1 || !gradientColor2) {
          toast.error("Please select both gradient colors");
          return;
        }
      }
      if (backgroundType === "solid" && !backgroundColor) {
        toast.error("Please select a background color");
        return;
      }
    }

    try {
      const parseDateTimeString = (dateTimeString) => {
        if (!dateTimeString || dateTimeString.trim() === "") return null;
        try {
          const pattern =
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i;
          const match = dateTimeString.trim().match(pattern);
          if (!match) return null;
          const [, month, day, year, hour, minute, ampm] = match;
          let hours24 = parseInt(hour, 10);
          const minutes = parseInt(minute, 10);
          if (ampm.toUpperCase() === "PM" && hours24 !== 12) {
            hours24 += 12;
          } else if (ampm.toUpperCase() === "AM" && hours24 === 12) {
            hours24 = 0;
          }
          const date = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
            hours24,
            minutes
          );
          if (isNaN(date.getTime())) return null;
          return date;
        } catch (error) {
          return null;
        }
      };

      const formData = new FormData();
      formData.append("roomName", meetingName.trim());
      formData.append("roomType", roomType);

      let timezoneValue = "America/New_York";
      if (selectedTimezone) {
        if (typeof selectedTimezone === "string") {
          if (selectedTimezone.includes("(")) {
            timezoneValue = selectedTimezone.split("(")[0].trim();
          } else {
            timezoneValue = selectedTimezone.split(" ")[0];
          }
        }
      }
      formData.append("timezone", timezoneValue);

      if (!isnow && meetingStartTime) {
        const startTimePattern =
          /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i;
        const match = meetingStartTime.trim().match(startTimePattern);

        if (match) {
          const [, month, day, year, hour, minute, ampm] = match;
          formData.append("startTimeHour", hour);
          formData.append("startTimeMinute", minute);
          formData.append("StartTimeAmPm", ampm.toUpperCase());
        }

        const startDate = parseDateTimeString(meetingStartTime);
        const endDate = meetingEndTime
          ? parseDateTimeString(meetingEndTime)
          : null;

        if (startDate && endDate) {
          const diffMs = endDate.getTime() - startDate.getTime();
          const durationMinutes = Math.round(diffMs / (1000 * 60));
          if (durationMinutes > 0) {
            formData.append("duration", durationMinutes.toString());
          }
        }

        if (startDate) {
          formData.append("startTime", startDate.toISOString());
        }
      } else {
        formData.append("startTime", new Date().toISOString());
      }

      if (!isnow && meetingEndTime) {
        const endDate = parseDateTimeString(meetingEndTime);
        if (endDate) {
          formData.append("endTime", endDate.toISOString());
        }
      } else {
        formData.append(
          "endTime",
          new Date(new Date().getTime() + 30 * 60 * 1000).toISOString()
        );
      }

      Object.keys(roomSettings).forEach((key) => {
        const value = roomSettings[key];
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      if (elementPresets.length > 0) {
        formData.append("presets", JSON.stringify(elementPresets));
      }

      if (invitesList.length > 0) {
        formData.append("emails", JSON.stringify(invitesList));
      }

      if (speakersList.length > 0) {
        formData.append("speakers", JSON.stringify(speakersList));
      }

      formData.append("exitUrlEnabled", exitUrlEnabled);
      if (exitUrlEnabled && exitUrl.trim()) {
        formData.append("exitUrl", exitUrl.trim());
      }

      formData.append("customButtonEnabled", customButtonEnabled);
      if (customButtonEnabled) {
        formData.append("customButtonText", customButtonText.trim());
        formData.append("customButtonUrl", customButtonUrl.trim());
      }

      formData.append("waitingRoomTextEnabled", waitingRoomTextEnabled);
      if (waitingRoomTextEnabled) {
        formData.append("waitingRoomTitle", waitingRoomTitle.trim());
        formData.append("waitingRoomMessage", waitingRoomMessage.trim());
        formData.append("waitingRoomSubText", waitingRoomSubText.trim());
      }

      formData.append("customBackgroundEnabled", customBackgroundEnabled);

      if (customBackgroundEnabled) {
        formData.append("backgroundType", backgroundType);

        if (backgroundType === "solid") {
          formData.append("backgroundColor", backgroundColor);
        } else if (backgroundType === "gradient") {
          formData.append("gradientColor1", gradientColor1);
          formData.append("gradientColor2", gradientColor2);
          formData.append("gradientDirection", gradientDirection);
        } else if (backgroundType === "image" && backgroundImage) {
          formData.append("backgroundImage", backgroundImage);
        }
      }

      const response = await createScheduledMeeting(formData);

      toast.success(`${roomType} created successfully!`);

      await getRooms();

      // Reset all form fields
      setMeetingName("");
      setMeetingStartTime("");
      setMeetingEndTime("");
      setInvitesEmailInput("");
      setInvitesList([]);
      setSpeakersList([]);

      setRoomSettings({
        allowChat: true,
        allowScreenSharing: false,
        muteParticipantsOnEntry: false,
        allowReactions: true,
        allowRaiseHand: true,
        requirePassword: false,
        password: "",
        waitingRoom: false,
        saveAsPreset: false,
        presetName: "",
      });

      setExitUrlEnabled(false);
      setExitUrl("");
      setCustomButtonEnabled(false);
      setCustomButtonText("");
      setCustomButtonUrl("");
      setWaitingRoomTextEnabled(false);
      setWaitingRoomTitle("");
      setWaitingRoomMessage("");
      setWaitingRoomSubText("");
      setCustomBackgroundEnabled(false);
      setBackgroundType("solid");
      setBackgroundColor("#1a1a2e");
      setGradientColor1("#1a1a2e");
      setGradientColor2("#615fff");
      setGradientDirection("to right");
      setBackgroundImage(null);
      setBackgroundImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setShowItem(null);
      setElementPresets([]);

      if (isnow && response.data?.roomId) {
        window.open(`/${response.data.roomId}`, "_blank");
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create meeting. Please try again.";
      toast.error(errorMessage);
    }
  };

  const getPresetsByRoomId = async (roomId) => {
    try {
      const response = await folderService.getPresetsByRoomId(roomId);
      const presets = response.presets || response.data || [];
      setEditElementPresets(presets);
    } catch (error) {
      console.log("Error getting presets by room id:", error);
      setEditElementPresets([]);
    }
  };

  // Get upcoming sessions (future sessions)
  const upcomingSessions = rooms.filter((room) => {
    if (!room.scheduledStartTime) return false;
    return new Date(room.scheduledStartTime) > new Date();
  }).slice(0, 5);

  return (
    <div className="min-h-screen text-white flex">
      {/* Main Content */}
      <div className="flex-1">
        <div className="max-w-6xl min-h-screen mx-auto space-y-6 w-full px-4 md:px-6 py-4 md:py-6">
          {/* Main Dashboard View - Only show when no form is active */}
          {!showItem && (
            <>
              {/* Welcome Header */}
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 font-sans">
                  Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
                </h2>
                <p className="text-sm md:text-base text-gray-400 font-sans">
                  Here's what's happening with your events
                </p>
              </div>

              {/* Quick Actions - 3 Primary Lanes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Start Button */}
                <button
                  onClick={() => navigate("/dashboard/start")}
                  className="p-4 md:p-6 rounded-2xl bg-gradient-to-br from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-primary,#8b44e0)] text-white hover:shadow-xl transition-all text-left"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <PlayCircle className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg md:text-xl font-bold mb-0.5 font-sans">Start</h3>
                      <p className="text-xs md:text-sm opacity-90 font-sans">Meeting, Webinar, Live Stream</p>
                    </div>
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6 opacity-80" />
                  </div>
                </button>

                {/* Schedule Button */}
                <button
                  onClick={() => navigate("/dashboard/schedule")}
                  className="p-4 md:p-6 rounded-2xl bg-gradient-to-br from-[#ffa500] to-[#ff8c00] text-white hover:shadow-xl transition-all text-left"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg md:text-xl font-bold mb-0.5 font-sans">Schedule</h3>
                      <p className="text-xs md:text-sm opacity-90 font-sans">Meeting, Webinar, Live Stream</p>
                    </div>
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6 opacity-80" />
                  </div>
                </button>

                {/* Room Setup Button */}
                <button
                  onClick={() => navigate("/dashboard/rooms")}
                  className="p-4 md:p-6 rounded-2xl bg-gradient-to-br from-[#00c853] to-[var(--bk-accent,#00a844)] text-white hover:shadow-xl transition-all text-left"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Video className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg md:text-xl font-bold mb-0.5 font-sans">Room Setup</h3>
                      <p className="text-xs md:text-sm opacity-90 font-sans">Configure Spaces</p>
                    </div>
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6 opacity-80" />
                  </div>
                </button>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  icon={Calendar}
                  label="Today's Sessions"
                  value={stats.todaySessions}
                  color="text-blue-400"
                  bgColor="bg-blue-500/10"
                />
                <StatCard
                  icon={Clock}
                  label="Upcoming"
                  value={stats.upcomingSessions}
                  color="text-purple-400"
                  bgColor="bg-purple-500/10"
                />
                <StatCard
                  icon={Video}
                  label="Active Rooms"
                  value={stats.activeRooms}
                  color="text-green-400"
                  bgColor="bg-green-500/10"
                />
                <StatCard
                  icon={Activity}
                  label="Total Recordings"
                  value={stats.totalRecordings}
                  color="text-orange-400"
                  bgColor="bg-orange-500/10"
                />
              </div>

              {/* Upcoming Sessions */}
              <div className="bg-white/5 rounded-xl border border-white/10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2 font-sans">
                    <Calendar className="w-5 h-5 text-purple-400" />
                    Upcoming Sessions
                  </h3>
                  <button
                    onClick={() => navigate("/dashboard/sessions")}
                    className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 font-sans"
                  >
                    View All
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {upcomingSessions.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-4 font-sans">No upcoming sessions</p>
                    <button
                      onClick={handleCreateMeeting}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] text-white rounded-lg hover:opacity-90 transition-opacity font-sans"
                    >
                      <Plus className="w-4 h-4" />
                      Schedule Your First Session
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingSessions.map((session) => (
                      <div
                        key={session._id || session.id}
                        className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            session.roomType === "Webinar"
                              ? "bg-blue-500/20 text-blue-400"
                              : "bg-purple-500/20 text-purple-400"
                          }`}
                        >
                          <Video className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium truncate font-sans">
                            {session.roomName}
                          </h4>
                          <div className="flex items-center gap-3 text-sm text-gray-400 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {session.scheduledStartTime
                                ? new Date(session.scheduledStartTime).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "-"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {session.scheduledStartTime
                                ? new Date(session.scheduledStartTime).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    timeZoneName: "short",
                                  })
                                : "-"}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleEditRoom(session)}
                          className="px-3 py-1.5 text-sm border border-white/10 text-white hover:bg-white/10 rounded-lg transition-colors font-sans"
                        >
                          Details
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Activity & Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-xl border border-white/10 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2 font-sans">
                    <Activity className="w-5 h-5 text-green-400" />
                    Usage This Month
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-sans">Recording Time</span>
                      <span className="text-white font-medium font-sans">{Math.round(monthlyUsage.recordingMinutes)} min</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-sans">Storage Used</span>
                      <span className="text-white font-medium font-sans">{monthlyUsage.storageGB.toFixed(2)} GB</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-sans">Active Rooms</span>
                      <span className="text-white font-medium font-sans">{monthlyUsage.rooms}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl border border-white/10 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2 font-sans">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                    Recent Recordings
                  </h3>
                  {recordings.length === 0 ? (
                    <p className="text-gray-400 text-sm font-sans">No recordings yet</p>
                  ) : (
                    <div className="space-y-2">
                      {recordings.slice(0, 3).map((recording) => (
                        <div key={recording._id || recording.id} className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                            <Video className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate font-sans">{recording.title || "Recording"}</p>
                            <p className="text-xs text-gray-500 font-sans">
                              {recording.createdAt
                                ? new Date(recording.createdAt).toLocaleString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                    timeZoneName: "short",
                                  })
                                : "-"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Banner Section for Schedule Meeting */}
          {showItem && showItem === "meeting" && (
            <div className="relative mb-6 md:mb-8 rounded-xl overflow-hidden">
              <div className="absolute inset-0 opacity-30" />

              {/* Back Button */}
              <button
                onClick={() => setShowItem(null)}
                className="absolute top-4 left-4 z-20 p-2 rounded-lg bg-black/30 hover:bg-black/50 text-white transition-colors backdrop-blur-sm"
                aria-label="Go back"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Content */}
              <div className="primary-card-background relative z-10 flex flex-col items-center justify-center gap-4 md:gap-6 p-6 md:p-8">
                <h1 className="text-2xl font-bold text-white tracking-wide font-sans">
                  SCHEDULE A MEETING
                </h1>
                <img
                  src="/icons/scheduleMeeting.svg"
                  alt="Schedule Meeting"
                  className="h-24 w-24 text-white"
                />
              </div>
            </div>
          )}

          {/* Banner Section for Schedule webinar */}
          {user?.plan === "Business" && showItem && showItem === "webinar" && (
            <div className="relative mb-6 md:mb-8 rounded-xl overflow-hidden">
              <div className="absolute inset-0 opacity-30" />

              {/* Back Button */}
              <button
                onClick={() => setShowItem(null)}
                className="absolute top-4 left-4 z-20 p-2 rounded-lg bg-black/30 hover:bg-black/50 text-white transition-colors backdrop-blur-sm"
                aria-label="Go back"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Content */}
              <div className="primary-card-background relative z-10 flex flex-col items-center justify-center gap-4 md:gap-6 p-6 md:p-8">
                <h1 className="text-2xl font-bold text-white tracking-wide font-sans">
                  SCHEDULE A WEBINAR
                </h1>
                <img
                  src="/icons/scheduleWebinar.svg"
                  alt="Schedule Meeting"
                  className="h-24 w-24 text-white"
                />
              </div>
            </div>
          )}

          {/* Room Settings Form */}
          {showItem && showItem === "meeting" && (
            <ScheduleMeetingForm
              meetingName={meetingName}
              setMeetingName={setMeetingName}
              meetingStartTime={meetingStartTime}
              setMeetingStartTime={setMeetingStartTime}
              meetingEndTime={meetingEndTime}
              setMeetingEndTime={setMeetingEndTime}
              selectedTimezone={selectedTimezone}
              setSelectedTimezone={setSelectedTimezone}
              invitesEmailInput={invitesEmailInput}
              setInvitesEmailInput={setInvitesEmailInput}
              invitesList={invitesList}
              setInvitesList={setInvitesList}
              speakersList={speakersList}
              setSpeakersList={setSpeakersList}
              roomSettings={roomSettings}
              handleSettingChange={handleSettingChange}
              customBackgroundEnabled={customBackgroundEnabled}
              setCustomBackgroundEnabled={setCustomBackgroundEnabled}
              backgroundType={backgroundType}
              setBackgroundType={setBackgroundType}
              backgroundColor={backgroundColor}
              setBackgroundColor={setBackgroundColor}
              gradientColor1={gradientColor1}
              setGradientColor1={setGradientColor1}
              gradientColor2={gradientColor2}
              setGradientColor2={setGradientColor2}
              gradientDirection={gradientDirection}
              setGradientDirection={setGradientDirection}
              backgroundImage={backgroundImage}
              setBackgroundImage={setBackgroundImage}
              backgroundImagePreview={backgroundImagePreview}
              setBackgroundImagePreview={setBackgroundImagePreview}
              fileInputRef={fileInputRef}
              exitUrlEnabled={exitUrlEnabled}
              setExitUrlEnabled={setExitUrlEnabled}
              exitUrl={exitUrl}
              setExitUrl={setExitUrl}
              customButtonEnabled={customButtonEnabled}
              setCustomButtonEnabled={setCustomButtonEnabled}
              customButtonUrl={customButtonUrl}
              setCustomButtonUrl={setCustomButtonUrl}
              customButtonText={customButtonText}
              setCustomButtonText={setCustomButtonText}
              waitingRoomTextEnabled={waitingRoomTextEnabled}
              setWaitingRoomTextEnabled={setWaitingRoomTextEnabled}
              waitingRoomTitle={waitingRoomTitle}
              setWaitingRoomTitle={setWaitingRoomTitle}
              waitingRoomMessage={waitingRoomMessage}
              setWaitingRoomMessage={setWaitingRoomMessage}
              waitingRoomSubText={waitingRoomSubText}
              setWaitingRoomSubText={setWaitingRoomSubText}
              isValidUrl={isValidUrl}
              handleStartMeeting={handleStartMeeting}
            />
          )}

          {/* Webinar Settings Form */}
          {showItem && showItem === "webinar" && (
            <WebinarScheduleForm
              meetingName={meetingName}
              setMeetingName={setMeetingName}
              meetingStartTime={meetingStartTime}
              setMeetingStartTime={setMeetingStartTime}
              meetingEndTime={meetingEndTime}
              setMeetingEndTime={setMeetingEndTime}
              invitesEmailInput={invitesEmailInput}
              setInvitesEmailInput={setInvitesEmailInput}
              invitesList={invitesList}
              setInvitesList={setInvitesList}
              roomSettings={roomSettings}
              handleSettingChange={handleSettingChange}
              customBackgroundEnabled={customBackgroundEnabled}
              setCustomBackgroundEnabled={setCustomBackgroundEnabled}
              backgroundType={backgroundType}
              setBackgroundType={setBackgroundType}
              backgroundColor={backgroundColor}
              setBackgroundColor={setBackgroundColor}
              gradientColor1={gradientColor1}
              setGradientColor1={setGradientColor1}
              gradientColor2={gradientColor2}
              setGradientColor2={setGradientColor2}
              gradientDirection={gradientDirection}
              setGradientDirection={setGradientDirection}
              backgroundImage={backgroundImage}
              setBackgroundImage={setBackgroundImage}
              backgroundImagePreview={backgroundImagePreview}
              setBackgroundImagePreview={setBackgroundImagePreview}
              fileInputRef={fileInputRef}
              exitUrlEnabled={exitUrlEnabled}
              setExitUrlEnabled={setExitUrlEnabled}
              exitUrl={exitUrl}
              setExitUrl={setExitUrl}
              customButtonEnabled={customButtonEnabled}
              setCustomButtonEnabled={setCustomButtonEnabled}
              customButtonUrl={customButtonUrl}
              setCustomButtonUrl={setCustomButtonUrl}
              customButtonText={customButtonText}
              setCustomButtonText={setCustomButtonText}
              waitingRoomTextEnabled={waitingRoomTextEnabled}
              setWaitingRoomTextEnabled={setWaitingRoomTextEnabled}
              waitingRoomTitle={waitingRoomTitle}
              setWaitingRoomTitle={setWaitingRoomTitle}
              waitingRoomMessage={waitingRoomMessage}
              setWaitingRoomMessage={setWaitingRoomMessage}
              waitingRoomSubText={waitingRoomSubText}
              setWaitingRoomSubText={setWaitingRoomSubText}
              isValidUrl={isValidUrl}
              handleStartMeeting={handleStartMeeting}
            />
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-lg p-6 md:p-8 w-full max-w-md"
              style={{ backgroundColor: "#3C3A4C" }}
            >
              <div className="flex flex-col items-center text-center">
                <AlertTriangle className="h-12 w-12 md:h-16 md:w-16 text-red-500 mb-3 md:mb-4" />
                <h3 className="text-lg md:text-xl font-bold text-white mb-2 font-sans">
                  Are you sure?
                </h3>
                <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6 font-sans">
                  Do you really want to delete the room "
                  {roomToDelete?.roomName}
                  "? This process cannot be undone.
                </p>
                <div className="flex flex-col md:flex-row justify-center gap-3 md:space-x-4 w-full md:w-auto">
                  <button
                    onClick={cancelDeleteRoom}
                    className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-500 transition-colors text-sm md:text-base font-sans"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteRoom}
                    className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm md:text-base font-sans"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Preset Modal */}
        {showPresetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-lg p-4 md:p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: "#3C3A4C" }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg md:text-xl font-bold text-white font-sans">
                  Add Element
                </h3>
                <button
                  onClick={() => setShowPresetModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                {/* Folders List */}
                <div className="w-full md:w-1/3">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2 font-sans">
                    Folders
                  </h4>
                  <div
                    className="space-y-2 p-2 rounded-lg max-h-48 md:max-h-full overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {folders.map((folder) => (
                      <button
                        key={folder._id}
                        onClick={() => setSelectedFolder(folder._id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-xs md:text-sm transition-colors font-sans ${
                          selectedFolder === folder._id
                            ? "bg-[var(--bk-primary,#6a1fbf)] text-white"
                            : "text-gray-300 hover:bg-gray-700"
                        }`}
                      >
                        <Folder size={16} className="inline mr-2" />
                        {folder.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Elements List */}
                <div className="w-full md:w-2/3">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2 font-sans">
                    Elements
                  </h4>
                  <div
                    className="space-y-2 p-2 rounded-lg h-64 md:h-96 overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {folders
                      .find((f) => f._id === selectedFolder)
                      ?.elements.map((element) => (
                        <div
                          key={element._id}
                          className="flex items-center justify-between p-2 md:p-3 rounded-md bg-gray-700"
                        >
                          <div className="flex items-center flex-1 min-w-0 mr-2">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-white text-black rounded-md flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                              {getWidgetIcon(element.type)}
                            </div>
                            <span className="text-white text-sm md:text-base truncate font-sans">
                              {element.name}
                            </span>
                          </div>
                          <button
                            onClick={() => selectPreset(element)}
                            className="px-2 md:px-3 py-1 rounded-md bg-[var(--bk-primary,#6a1fbf)] text-white text-xs md:text-sm hover:opacity-90 transition-colors flex-shrink-0"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Preset Modal */}
        {showEditPresetModal && editingRoom && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-lg p-4 md:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: "#3C3A4C" }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base md:text-xl font-bold text-white font-sans">
                  Edit Presets for {editingRoom.name}
                </h3>
                <button
                  onClick={() => setShowEditPresetModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
                {/* Available Elements */}
                <div className="w-full md:w-1/2">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2 font-sans">
                    Available Elements
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {folders.map((folder) => (
                      <button
                        key={folder._id}
                        onClick={() => setSelectedFolder(folder._id)}
                        className={`px-3 py-1 rounded-full text-xs md:text-sm transition-colors font-sans ${
                          selectedFolder === folder._id
                            ? "bg-[var(--bk-primary,#6a1fbf)] text-white"
                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }`}
                      >
                        {folder.name}
                      </button>
                    ))}
                  </div>
                  <div
                    className="space-y-2 p-2 rounded-lg h-64 md:h-96 overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {folders
                      .find((f) => f._id === selectedFolder)
                      ?.elements.map((element) => (
                        <div
                          key={element._id}
                          className="flex items-center justify-between p-2 md:p-3 rounded-md bg-gray-700"
                        >
                          <div className="flex items-center flex-1 min-w-0 mr-2">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-white text-black rounded-md flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                              {getWidgetIcon(element.type)}
                            </div>
                            <span className="text-white text-sm md:text-base truncate font-sans">
                              {element.name}
                            </span>
                          </div>
                          <button
                            onClick={() => handleAddPresetToRoom(element)}
                            className="px-2 md:px-3 py-1 rounded-md bg-[var(--bk-primary,#6a1fbf)] text-white text-xs md:text-sm hover:opacity-90 transition-colors flex-shrink-0"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Added Presets */}
                <div className="w-full md:w-1/2">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2 font-sans">
                    Added Presets
                  </h4>
                  <div
                    className="space-y-2 p-2 rounded-lg h-64 md:h-96 overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {editElementPresets.map((preset, index) => (
                      <div
                        key={preset.id || preset._id || index}
                        draggable
                        onDragStart={(e) => handleEditDragStart(e, index)}
                        onDragOver={(e) => handleEditDragOver(e, index)}
                        onDragLeave={handleEditDragLeave}
                        onDrop={(e) => handleEditDrop(e, index)}
                        onDragEnd={handleEditDragEnd}
                        className={`relative flex items-center justify-between p-2 md:p-3 rounded-md cursor-move transition-all duration-200 ${
                          editDraggedItem === index
                            ? "opacity-50 scale-95"
                            : editDragOverIndex === index
                            ? "border-[var(--bk-primary,#6a1fbf)] bg-[var(--bk-primary,#6a1fbf)]/20"
                            : "bg-gray-700 hover:bg-gray-600"
                        }`}
                      >
                        <div className="flex items-center flex-1 min-w-0 mr-2">
                          <div className="w-7 h-7 md:w-8 md:h-8 bg-white text-black rounded-md flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                            {getWidgetIcon(preset.type)}
                          </div>
                          <span className="text-white text-sm md:text-base truncate font-sans">
                            {preset.name}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemovePresetFromRoom(preset)}
                          className="text-gray-400 hover:text-white flex-shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-end mt-4 md:mt-6 gap-3 md:gap-0">
                <button
                  onClick={() => setShowEditPresetModal(false)}
                  className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-500 transition-colors md:mr-4 text-sm md:text-base font-sans"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRoomPresets}
                  className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-[var(--bk-primary,#6a1fbf)] text-white hover:opacity-90 transition-colors text-sm md:text-base font-sans"
                >
                  Save Presets
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
